// admin-client/src/pages/Calendar.jsx
// =============================================================================
// Per-device, per-month, daily-row calendar (hours = Y axis, days = X axis).
// All times are rendered in 24-hour format (see ../lib/datetime).
//
// Cell clicks:
//   • Empty slot       → open NewSessionModal (admin backfills an actual sign-in)
//   • Has session      → open EditSessionModal (modify times / CathayID / status)
//   • Booking only     → open EditBookingModal (modify the booking row)
// =============================================================================

import { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../lib/api.js';
import { roundSignIn, roundSignOut } from '@iptbc/session-rounding';
import { fmtDate, fmtTime, fmtDateTime, toDtLocalInput, fromDtLocalInput, partsInActiveTz } from '../lib/datetime.js';
import { usePanScroll } from '../components/usePanScroll.js';

const TODAY = new Date();
const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysInMonth = (y, m1) => new Date(y, m1, 0).getDate();
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_COL_W = 92;
const HOUR_ROW_H = 52;

const FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'booked', label: 'Booked crew' },
  { id: 'actual', label: 'Actual crew' },
];

export default function Calendar() {
  const [month, setMonth] = useState(monthKeyOf(TODAY));
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [filter, setFilter] = useState('all');
  // Grid vs Daily-list view — they share the same fill-the-viewport area
  // so the calendar's bottom scrollbar always sits at the visible viewport
  // edge (the page itself doesn't scroll on this view).
  const [view, setView] = useState('grid');
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // { kind, row?, prefill? }

  const [year, mon] = month.split('-').map(Number);
  const dim = daysInMonth(year, mon);

  const refresh = useCallback(async () => {
    try {
      const [d, b, s] = await Promise.all([
        api.devices.list(),
        api.bookings.list({ month }),
        api.sessions.list({ month }),
      ]);
      setDevices(d.devices);
      setBookings(b.bookings.filter((row) => !row.staged));
      setSessions(s.sessions);
    } catch (e) {
      setErr(e.message);
    }
  }, [month]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!deviceId && devices.length > 0) setDeviceId(devices[0].id);
  }, [devices, deviceId]);

  const deviceBookings = useMemo(
    () => bookings.filter((b) => !b.ipt_device_id || b.ipt_device_id === deviceId),
    [bookings, deviceId]
  );
  const deviceSessions = useMemo(
    () => sessions.filter((s) => s.device_id === deviceId),
    [sessions, deviceId]
  );

  const cellIndex = useMemo(() => {
    const idx = {};
    const touch = (k) => (idx[k] = idx[k] || { bookings: [], sessions: [] });
    for (const b of deviceBookings) {
      // booking_date arrives from the API as an ISO string. Treat as a
      // pure date — derive YYYY-MM-DD without going through Date math
      // so browser timezone can't shift the displayed day.
      const baseIso = String(b.booking_date).slice(0, 10); // 'YYYY-MM-DD'
      const [by, bm, bd] = baseIso.split('-').map(Number);
      for (let h = b.start_hour; h < b.end_hour; h++) {
        const dayOffset = h >= 24 ? 1 : 0;
        const hour = h % 24;
        // Use Date.UTC + getUTC* to avoid local-tz day shifts.
        const ms = Date.UTC(by, bm - 1, bd) + dayOffset * 86400_000;
        const dt = new Date(ms);
        const dayStr = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
        touch(`${dayStr}|${hour}`).bookings.push(b);
      }
    }
    for (const s of deviceSessions) {
      const start = new Date(s.signin_rounded_hour);
      const end = s.signout_rounded_hour
        ? new Date(s.signout_rounded_hour)
        : new Date(start.getTime() + 3600_000);
      // Use the configured app timezone (HK by default) for cell
      // positioning, not the browser's local. Otherwise a session
      // stored at HK 00:00 on April 1 (= UTC 16:00 March 31) gets
      // bucketed under March 31 in any non-HK browser.
      for (let t = start.getTime(); t < end.getTime(); t += 3600_000) {
        const p = partsInActiveTz(new Date(t));
        if (p) touch(`${p.date}|${p.hour}`).sessions.push(s);
      }
      if (start.getTime() === end.getTime()) {
        const p = partsInActiveTz(start);
        if (p) touch(`${p.date}|${p.hour}`).sessions.push(s);
      }
    }
    return idx;
  }, [deviceBookings, deviceSessions]);

  // For each booked cell, work out which edges of the green "session block"
  // border to draw. A cell belongs to a block when it has a booking for a
  // CathayID that the previous and/or next hour also shares (consecutive
  // slots = one session). The top edge is drawn when the previous hour
  // doesn't share any CathayID, similarly for the bottom edge. Left/right
  // edges are always drawn on a booked cell. Wrap-around at midnight is
  // handled by hopping to hour 23 / hour 0 of the adjacent day.
  const blockEdges = useMemo(() => {
    const edges = {};
    const shifted = (dayKey, byDays) => {
      const d = new Date(dayKey);
      d.setDate(d.getDate() + byDays);
      return ymd(d);
    };
    const idsAt = (key) => {
      const c = cellIndex[key];
      if (!c) return new Set();
      return new Set(c.bookings.map((b) => String(b.cathay_id).toLowerCase()));
    };
    for (const [key, cell] of Object.entries(cellIndex)) {
      if (!cell.bookings.length) continue;
      const [dayKey, hourStr] = key.split('|');
      const h = parseInt(hourStr, 10);
      const myIds = idsAt(key);
      const prevKey = h > 0 ? `${dayKey}|${h - 1}` : `${shifted(dayKey, -1)}|23`;
      const nextKey = h < 23 ? `${dayKey}|${h + 1}` : `${shifted(dayKey, 1)}|0`;
      const prevIds = idsAt(prevKey);
      const nextIds = idsAt(nextKey);
      let prevShares = false;
      let nextShares = false;
      for (const id of myIds) {
        if (prevIds.has(id)) prevShares = true;
        if (nextIds.has(id)) nextShares = true;
      }
      edges[key] = { top: !prevShares, bottom: !nextShares };
    }
    return edges;
  }, [cellIndex]);

  // For each booked (cell, pilot), classify the slot relative to its
  // consecutive-slot block:
  //   'attended'   — pilot signed in at this very slot.
  //   'under_used' — pilot did sign in somewhere ELSE in the same block,
  //                  just not this slot. (Booked 3h, used 2h.)
  //   'no_show'    — pilot didn't sign in anywhere in the block.
  // Cells without bookings are absent from this map. We walk the block by
  // hopping ±1 hour while the same CathayID appears in the adjacent cell,
  // wrapping across midnight.
  const blockState = useMemo(() => {
    const out = {};
    const visited = new Set();
    const shiftedDay = (dayKey, by) => {
      const d = new Date(dayKey);
      d.setDate(d.getDate() + by);
      return ymd(d);
    };
    const step = (dayKey, hour, delta) => {
      const next = hour + delta;
      if (next < 0) return { dayKey: shiftedDay(dayKey, -1), hour: 23 };
      if (next > 23) return { dayKey: shiftedDay(dayKey, 1), hour: 0 };
      return { dayKey, hour: next };
    };
    const cellHasBooking = (key, cid) => {
      const c = cellIndex[key];
      if (!c) return false;
      return c.bookings.some((b) => String(b.cathay_id).toLowerCase() === cid);
    };
    const cellHasSession = (key, cid) => {
      const c = cellIndex[key];
      if (!c) return false;
      return c.sessions.some((s) => String(s.cathay_id_entered).toLowerCase() === cid);
    };

    for (const [key, cell] of Object.entries(cellIndex)) {
      if (!cell.bookings.length) continue;
      const [dayKey, hourStr] = key.split('|');
      const h = parseInt(hourStr, 10);
      for (const b of cell.bookings) {
        const cid = String(b.cathay_id).toLowerCase();
        const memberKey = `${key}|${cid}`;
        if (visited.has(memberKey)) continue;

        // Walk backwards while the previous hour also has this pilot booked.
        let lo = { dayKey, hour: h };
        while (true) {
          const prev = step(lo.dayKey, lo.hour, -1);
          if (cellHasBooking(`${prev.dayKey}|${prev.hour}`, cid)) lo = prev; else break;
        }
        // Walk forwards similarly.
        let hi = { dayKey, hour: h };
        while (true) {
          const next = step(hi.dayKey, hi.hour, 1);
          if (cellHasBooking(`${next.dayKey}|${next.hour}`, cid)) hi = next; else break;
        }
        // Enumerate every slot in [lo, hi].
        const blockKeys = [];
        let cur = { ...lo };
        while (true) {
          blockKeys.push(`${cur.dayKey}|${cur.hour}`);
          if (cur.dayKey === hi.dayKey && cur.hour === hi.hour) break;
          cur = step(cur.dayKey, cur.hour, 1);
        }
        const anySessionInBlock = blockKeys.some((k) => cellHasSession(k, cid));
        for (const k of blockKeys) {
          const mk = `${k}|${cid}`;
          visited.add(mk);
          const here = cellHasSession(k, cid);
          out[mk] = here ? 'attended' : (anySessionInBlock ? 'under_used' : 'no_show');
        }
      }
    }
    return out;
  }, [cellIndex]);

  const nowMs = Date.now();
  const selectedDevice = devices.find((d) => d.id === deviceId);

  // Right-click-drag panning + always-visible scrollbar plumbing. The
  // scrollable container is sized to fill the remaining viewport (see
  // h-[calc(100vh-...)] below) so the horizontal scrollbar sits near the
  // viewport bottom and is always visible without page scrolling.
  const pan = usePanScroll();

  return (
    // Lock the page to the viewport so the calendar's own scroll container
    // (below) gets to keep its bottom scrollbar inside the visible area.
    // Without this, the DayList below the grid made the whole page scroll
    // and the bottom scrollbar drifted below the fold.
    //   Height budget = 100vh
    //     − 5.5rem  Layout header (cathay-700 bar + cathay-800 nav)
    //     − 3rem    Layout main py-6 (1.5rem top + 1.5rem bottom)
    //   = 100vh − 8.5rem.
    <div className="flex flex-col gap-3 h-[calc(100vh-8.5rem)] overflow-hidden">
      <header className="flex items-end justify-between flex-wrap gap-3 shrink-0">
        <div>
          <h1 className="text-2xl font-semibold text-cathay-700">Calendar</h1>
          <p className="text-sm text-slate-600">
            Per-IPT monthly view. Click any empty slot to add an actual sign-in; click a populated
            slot to edit. All times shown in 24-hour format.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="text-xs text-slate-500">Device</div>
            <select
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              className="mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm min-w-[160px]"
            >
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} {d.status === 'active' ? '' : `(${d.status})`}
                </option>
              ))}
            </select>
          </label>

          {/* View pills — Grid (default) shows the daily-row table, List
              shows the per-day text breakdown. Both fill the same flex-1
              region below so the calendar's bottom scrollbar stays
              anchored to the viewport. */}
          <div className="text-sm">
            <div className="text-xs text-slate-500">View</div>
            <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
              <button
                onClick={() => setView('grid')}
                className={
                  'px-3 py-1.5 transition ' +
                  (view === 'grid' ? 'bg-cathay-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50')
                }
              >
                Grid
              </button>
              <button
                onClick={() => setView('list')}
                className={
                  'px-3 py-1.5 transition ' +
                  (view === 'list' ? 'bg-cathay-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50')
                }
              >
                Daily list
              </button>
            </div>
          </div>

          {/* Filter pills sit between Device and Month per spec — saves
              the vertical row that used to live below the header. The
              wrapping div mirrors the height of the other labelled
              controls so the bottom edges line up. */}
          <div className="text-sm">
            <div className="text-xs text-slate-500">Show</div>
            <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={
                    'px-3 py-1.5 transition ' +
                    (filter === f.id ? 'bg-cathay-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50')
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={() => setMonth(monthKeyOf(new Date(year, mon - 2, 1)))} className="px-2 py-1.5 border border-slate-300 rounded text-sm">←</button>
          <label className="text-sm">
            <div className="text-xs text-slate-500">Month</div>
            <input
              pattern="\d{4}-\d{2}"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm w-28"
            />
          </label>
          <button onClick={() => setMonth(monthKeyOf(new Date(year, mon, 1)))} className="px-2 py-1.5 border border-slate-300 rounded text-sm">→</button>
        </div>
      </header>

      <div className="shrink-0">
        <Legend filter={filter} />
      </div>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm shrink-0">{err}</div>}

      {!selectedDevice ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded text-sm shrink-0">
          Create at least one device on the <strong>Devices</strong> page to see a calendar.
        </div>
      ) : view === 'grid' ? (
        <div
          ref={pan.ref}
          onMouseDown={pan.onMouseDown}
          onContextMenu={pan.onContextMenu}
          title="Right-click and drag to pan the calendar"
          className="bg-white rounded shadow-sm border overflow-auto flex-1 min-h-0"
        >
          <table className="text-[11px] border-collapse">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className="sticky left-0 top-0 z-30 bg-slate-50 border-r border-slate-200 px-2 py-1.5 text-slate-600 text-left font-mono"
                  style={{ width: 96, minWidth: 96 }}
                >
                  Hour
                </th>
                {Array.from({ length: dim }, (_, i) => {
                  const day = i + 1;
                  const dt = new Date(year, mon - 1, day);
                  const w = dt.getDay();
                  return (
                    <th
                      key={`wk-${day}`}
                      className={
                        'sticky top-0 z-20 px-0 py-1 text-center border-l border-slate-100 ' +
                        ((w === 0 || w === 6) ? 'bg-slate-100 text-slate-600' : 'bg-slate-50 text-slate-500')
                      }
                      style={{ width: DAY_COL_W, minWidth: DAY_COL_W }}
                    >
                      {WEEKDAYS[w]}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {Array.from({ length: dim }, (_, i) => {
                  const day = i + 1;
                  const dt = new Date(year, mon - 1, day);
                  const isToday = ymd(dt) === ymd(new Date());
                  const w = dt.getDay();
                  return (
                    <th
                      key={`d-${day}`}
                      className={
                        'sticky z-20 px-0 py-1 text-center border-l border-slate-100 font-medium ' +
                        ((w === 0 || w === 6) ? 'bg-slate-100 ' : 'bg-slate-50 ') +
                        (isToday ? 'text-cathay-700 underline' : 'text-slate-700')
                      }
                      style={{ top: 28, width: DAY_COL_W, minWidth: DAY_COL_W }}
                    >
                      {day}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 24 }, (_, h) => (
                <tr key={`h-${h}`} className="border-t border-slate-100">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-white border-r border-slate-200 px-2 py-1 text-left text-slate-600 font-mono whitespace-nowrap"
                    style={{ width: 96, minWidth: 96, height: HOUR_ROW_H }}
                  >
                    {String(h).padStart(2, '0')}:00–{String((h + 1) % 24).padStart(2, '0')}:00
                  </th>
                  {Array.from({ length: dim }, (_, i) => {
                    const day = i + 1;
                    const dayDate = new Date(year, mon - 1, day);
                    const dayKey = ymd(dayDate);
                    const isToday = dayKey === ymd(new Date());
                    const w = dayDate.getDay();
                    const cell = cellIndex[`${dayKey}|${h}`];
                    const bookingsAtCell = cell?.bookings || [];
                    const sessionsAtCell = cell?.sessions || [];
                    const showBooked = filter === 'all' || filter === 'booked';
                    const showActual = filter === 'all' || filter === 'actual';

                    const bookedIds = bookingsAtCell.map((b) => String(b.cathay_id).toLowerCase());
                    const sessionIds = sessionsAtCell.map((s) => String(s.cathay_id_entered).toLowerCase());
                    const mismatch =
                      filter === 'all' &&
                      bookingsAtCell.length > 0 &&
                      sessionsAtCell.length > 0 &&
                      !bookedIds.some((id) => sessionIds.includes(id));

                    const slotEndMs = new Date(year, mon - 1, day, h + 1).getTime();
                    const inPast = slotEndMs <= nowMs;

                    // Walk every booking on the cell and bucket by block state.
                    // A cell counts as under-used if at least one booked pilot
                    // on the slot did sign in somewhere in the block but not
                    // here; it's a no-show only when every booked pilot on
                    // this cell skipped the whole block.
                    let cellHasUnderUsed = false;
                    let cellAllNoShow = bookingsAtCell.length > 0;
                    for (const b of bookingsAtCell) {
                      const st = blockState[`${dayKey}|${h}|${String(b.cathay_id).toLowerCase()}`];
                      if (st === 'under_used') cellHasUnderUsed = true;
                      if (st !== 'no_show') cellAllNoShow = false;
                    }
                    const underUsed = filter === 'all' && cellHasUnderUsed && bookingsAtCell.length > 0;
                    const noShow =
                      filter === 'all' &&
                      bookingsAtCell.length > 0 &&
                      cellAllNoShow &&
                      inPast;

                    const cls = [
                      'border-l border-slate-100 cursor-pointer transition align-middle text-center p-0.5',
                      !bookingsAtCell.length && !sessionsAtCell.length && (w === 0 || w === 6) ? 'bg-slate-50/60' : '',
                      noShow ? 'bg-red-100' : '',
                      // Under-use → light yellow background so it reads as a
                      // milder problem than no-show, and DOESN'T get the same
                      // alarming pink colour as a missed booking.
                      !noShow && underUsed ? 'bg-yellow-100' : '',
                      mismatch ? 'ring-2 ring-orange-500 ring-inset' : '',
                      isToday && !bookingsAtCell.length && !sessionsAtCell.length ? 'ring-1 ring-cathay-200 ring-inset' : '',
                      !bookingsAtCell.length && !sessionsAtCell.length ? 'hover:bg-cathay-50' : 'hover:brightness-95',
                    ].join(' ');

                    // Green "session block" border. Drawn whenever the
                    // booked layer is visible AND the cell has a booking,
                    // with top/bottom edges suppressed when the adjacent
                    // hour shares a CathayID — so a 3-hour run reads as
                    // one rounded-corner-less rectangle around three cells.
                    let blockShadow = '';
                    if (showBooked && bookingsAtCell.length > 0) {
                      const edges = blockEdges[`${dayKey}|${h}`] || { top: true, bottom: true };
                      const COLOR = '#10b981'; // emerald-500
                      const parts = [
                        `inset 2px 0 0 0 ${COLOR}`,
                        `inset -2px 0 0 0 ${COLOR}`,
                      ];
                      if (edges.top) parts.push(`inset 0 2px 0 0 ${COLOR}`);
                      if (edges.bottom) parts.push(`inset 0 -2px 0 0 ${COLOR}`);
                      blockShadow = parts.join(', ');
                    }

                    const click = () => {
                      // If the slot has any data (sessions OR bookings), open
                      // the unified Cell editor so admins can browse every
                      // signed-in crew and the booked rows side-by-side via
                      // tabs. The "blank cell" case still jumps straight to
                      // the new-session form because that's the obvious
                      // intent on an empty hour.
                      if (sessionsAtCell.length || bookingsAtCell.length) {
                        setEditing({ kind: 'cell', day, hour: h });
                      } else {
                        setEditing({ kind: 'new-session', prefill: { day, hour: h } });
                      }
                    };

                    return (
                      <td
                        key={`${day}-${h}`}
                        className={cls}
                        onClick={click}
                        title={
                          !bookingsAtCell.length && !sessionsAtCell.length
                            ? 'Click to add actual crew'
                            : (mismatch ? 'Substitute (booked ≠ actual). ' : '') +
                              (noShow ? 'No-show. ' : '') +
                              (underUsed && !noShow ? 'Under-used slot (pilot signed in elsewhere in the block, not this hour). ' : '') +
                              'Click to edit'
                        }
                        style={{
                          width: DAY_COL_W,
                          minWidth: DAY_COL_W,
                          height: HOUR_ROW_H,
                          boxShadow: blockShadow || undefined,
                        }}
                      >
                        <div className="flex flex-col items-stretch gap-0.5">
                          {showBooked && bookingsAtCell.length > 0 && (
                            <Pill
                              kind="booked"
                              label={bookingsAtCell[0].cathay_id + (bookingsAtCell.length > 1 ? ` +${bookingsAtCell.length - 1}` : '')}
                              tooltip={`Booked: ${bookingsAtCell.map((b) => b.cathay_id).join(', ')}`}
                            />
                          )}
                          {showActual && sessionsAtCell.length > 0 && (
                            <Pill
                              kind="actual"
                              autoClosed={sessionsAtCell.some((s) => s.auto_closed)}
                              label={sessionsAtCell[0].cathay_id_entered + (sessionsAtCell.length > 1 ? ` +${sessionsAtCell.length - 1}` : '')}
                              tooltip={
                                sessionsAtCell.some((s) => s.auto_closed)
                                  ? `Session (auto-closed): ${sessionsAtCell.map((s) => s.cathay_id_entered + (s.auto_closed ? ' [auto]' : '')).join(', ')}`
                                  : `Session: ${sessionsAtCell.map((s) => s.cathay_id_entered).join(', ')}`
                              }
                            />
                          )}
                          {noShow && !showBooked && (
                            <span className="text-[10px] text-red-700 font-medium">no-show</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Daily-list view fills the same flex-1 region as the grid, so the
        // viewport-anchored scrollbar invariant holds either way.
        <div className="bg-white rounded shadow-sm border overflow-auto flex-1 min-h-0">
          <DayList year={year} mon={mon} dim={dim} cellIndex={cellIndex} setEditing={setEditing} />
        </div>
      )}

      {editing?.kind === 'cell' && selectedDevice && (() => {
        // Look up THIS cell's data on every render from the fresh cellIndex
        // so deletions / edits made via the sub-modals reflect immediately
        // when we bounce back here via returnTo.
        const dayDate = new Date(year, mon - 1, editing.day);
        const cell = cellIndex[`${ymd(dayDate)}|${editing.hour}`] || { bookings: [], sessions: [] };
        const cellRef = { kind: 'cell', day: editing.day, hour: editing.hour };
        return (
          <CellEditorModal
            day={editing.day}
            hour={editing.hour}
            deviceName={selectedDevice.name}
            cell={cell}
            onEditSession={(s) => setEditing({ kind: 'session', row: s, returnTo: cellRef })}
            onEditBooking={(b) => setEditing({ kind: 'booking', row: b, returnTo: cellRef })}
            onAddActual={() => setEditing({
              kind: 'new-session',
              prefill: { day: editing.day, hour: editing.hour, suggestedBooking: cell.bookings[0] },
              returnTo: cellRef,
            })}
            onChanged={refresh}
            onClose={() => setEditing(null)}
          />
        );
      })()}
      {editing?.kind === 'session' && (
        <EditSessionModal
          session={editing.row}
          onClose={() => setEditing(editing.returnTo || null)}
          onSaved={() => { refresh(); setEditing(editing.returnTo || null); }}
        />
      )}
      {editing?.kind === 'booking' && (
        <EditBookingModal
          booking={editing.row}
          devices={devices}
          onClose={() => setEditing(editing.returnTo || null)}
          onSaved={() => { refresh(); setEditing(editing.returnTo || null); }}
        />
      )}
      {editing?.kind === 'new-session' && (
        <NewSessionModal
          prefill={editing.prefill}
          year={year}
          mon={mon}
          deviceId={deviceId}
          deviceName={selectedDevice?.name}
          onClose={() => setEditing(editing.returnTo || null)}
          onSaved={() => { refresh(); setEditing(editing.returnTo || null); }}
        />
      )}
    </div>
  );
}

/* ---------------- pills / legend ---------------- */

function Pill({ kind, label, tooltip, autoClosed = false }) {
  const tones = {
    booked: 'bg-cathay-100 text-cathay-800 border-cathay-200',
    actual: 'bg-green-100 text-green-800 border-green-200',
    // Auto-closed sessions get a yellow tone so admins can see at a
    // glance which "actual" attendances were ended by the idle cron
    // rather than confirmed by a pilot sign-out.
    actual_auto: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };
  const tone = kind === 'actual' && autoClosed ? tones.actual_auto : tones[kind];
  return (
    <span
      title={tooltip}
      className={
        'inline-block font-mono text-[10px] leading-tight rounded-full border px-1.5 py-0.5 truncate ' +
        tone
      }
    >
      {label}
    </span>
  );
}

function Legend({ filter }) {
  return (
    // No overflow-x-auto here — that would clip absolute-positioned
    // children (the InfoTip tooltips), so the legend stays on one
    // logical row but can extend off-screen on very narrow viewports.
    <div className="text-xs text-slate-600 flex flex-nowrap gap-3 items-center whitespace-nowrap">
      <span className="inline-flex items-center gap-1 shrink-0">
        <span className="inline-block rounded-full border border-cathay-200 bg-cathay-100 px-1.5 py-0.5 font-mono text-[10px] text-cathay-800">CX12345</span>
        Booked
      </span>
      <span className="inline-flex items-center gap-1 shrink-0">
        <span className="inline-block rounded-full border border-green-200 bg-green-100 px-1.5 py-0.5 font-mono text-[10px] text-green-800">CX12345</span>
        Actual
      </span>
      <span className="inline-flex items-center gap-1 shrink-0">
        <span className="inline-block rounded-full border border-yellow-200 bg-yellow-100 px-1.5 py-0.5 font-mono text-[10px] text-yellow-800">CX12345</span>
        Auto-closed
        <InfoTip text="Actual session was force-closed by the 4-hour idle cron, not by a pilot sign-out — attendance for the whole window is uncertain." />
      </span>
      <span className="inline-flex items-center gap-1 shrink-0">
        <span
          className="inline-block w-4 h-4 rounded"
          style={{ boxShadow: 'inset 0 0 0 2px #10b981' }}
        />
        Session block
        <InfoTip text="Consecutive booked slots for the same pilot are wrapped as one session." />
      </span>
      {filter === 'all' && (
        <>
          <span className="inline-flex items-center gap-1 shrink-0">
            <span className="inline-block w-4 h-4 rounded ring-2 ring-orange-500 ring-inset" />
            Substitute
            <InfoTip text="A different CathayID signed in on the booked device + hour." />
          </span>
          <span className="inline-flex items-center gap-1 shrink-0">
            <span className="inline-block w-4 h-4 rounded bg-red-100" />
            No-show
            <InfoTip text="Past slot — booked but the pilot never signed in for any hour of the booking block." />
          </span>
          <span className="inline-flex items-center gap-1 shrink-0">
            <span className="inline-block w-4 h-4 rounded bg-yellow-100" />
            Under-used
            <InfoTip text="Pilot used some but not all booked hours of the block — this hour was booked but not used." />
          </span>
        </>
      )}
      <span className="text-slate-500 shrink-0">Empty cells → click to add sign-in</span>
    </div>
  );
}

/**
 * InfoTip — small (i) badge with a CSS-only popup tooltip on hover or
 * keyboard focus. Native `title` attributes are too slow / unreliable
 * (Chrome has a ~700ms delay and they don't appear in some environments
 * at all), so we render the tooltip inline as an absolutely-positioned
 * span keyed off the parent's `group-hover` / `group-focus-within` state.
 * Tailwind handles the visibility transitions.
 */
function InfoTip({ text }) {
  return (
    <span className="relative inline-flex group">
      <span
        aria-label={text}
        tabIndex={0}
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-700 text-[9px] font-bold leading-none cursor-help select-none"
      >
        i
      </span>
      <span
        role="tooltip"
        className="
          pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1
          opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
          transition-opacity duration-100
          bg-slate-800 text-white text-[11px] leading-snug font-normal
          rounded shadow-lg px-2 py-1.5 max-w-xs w-max z-50 whitespace-normal
        "
      >
        {text}
      </span>
    </span>
  );
}

/* ---------------- detail list ---------------- */

function DayList({ year, mon, dim, cellIndex, setEditing }) {
  const [listMode, setListMode] = useState('session'); // 'session' | 'slot'

  const bookingsByDay = {};
  const sessionsByDay = {};
  for (const [key, cell] of Object.entries(cellIndex)) {
    const day = key.split('|')[0];
    for (const b of cell.bookings) (bookingsByDay[day] = bookingsByDay[day] || new Map()).set(b.id, b);
    for (const s of cell.sessions) (sessionsByDay[day] = sessionsByDay[day] || new Map()).set(s.id, s);
  }
  return (
    <div className="p-4">
      <header className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-sm font-medium text-slate-800">
          Daily list (click rows to edit)
        </div>
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
          <button
            onClick={() => setListMode('session')}
            className={'px-3 py-1.5 transition ' +
              (listMode === 'session' ? 'bg-cathay-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50')}
          >
            Session view
          </button>
          <button
            onClick={() => setListMode('slot')}
            className={'px-3 py-1.5 transition ' +
              (listMode === 'slot' ? 'bg-cathay-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50')}
          >
            Individual slot view
          </button>
        </div>
      </header>
      <div className="space-y-4">
        {Array.from({ length: dim }, (_, i) => {
          const day = i + 1;
          const dayDate = new Date(year, mon - 1, day);
          const dayKey = ymd(dayDate);
          // Each mode shows EXACTLY ONE kind of row per day:
          //   session  — actual ipt_sessions only (each is already a session)
          //   slot     — per-hour booked rows only
          // No mixing — that's what the user found confusing.
          const slotRows = listMode === 'slot'
            ? Array.from((bookingsByDay[dayKey] || new Map()).values())
                .sort((a, b) => a.start_hour - b.start_hour)
            : [];
          const sessionRows = listMode === 'session'
            ? Array.from((sessionsByDay[dayKey] || new Map()).values())
                .sort((a, b) => new Date(a.signin_rounded_hour) - new Date(b.signin_rounded_hour))
            : [];
          if (slotRows.length === 0 && sessionRows.length === 0) return null;
          return (
            <div key={dayKey}>
              <div className="text-sm font-medium text-slate-700">
                {WEEKDAYS[dayDate.getDay()]} {fmtDate(dayDate)}
              </div>
              {listMode === 'slot' && (
                <table className="min-w-full text-xs mt-1">
                  <tbody>
                    {slotRows.map((b) => (
                      <tr key={`b-${b.id}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setEditing({ kind: 'booking', row: b })}>
                        <td className="py-1 w-32">
                          {String(b.start_hour).padStart(2, '0')}:00 – {String(b.end_hour % 24).padStart(2, '0')}:00
                          {b.end_hour > 24 ? ' (+1d)' : ''}
                        </td>
                        <td className="py-1 font-mono">{b.cathay_id}</td>
                        <td className="py-1 text-slate-500">{b.pilot_name || ''}</td>
                        <td className="py-1 text-cathay-700">booked</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {listMode === 'session' && (
                <table className="min-w-full text-xs mt-1">
                  <tbody>
                    {sessionRows.map((s) => (
                      <tr key={`s-${s.id}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setEditing({ kind: 'session', row: s })}>
                        <td className="py-1 w-32 text-green-700">
                          {fmtTime(s.signin_at_utc)} – {s.signout_at_utc ? fmtTime(s.signout_at_utc) : 'open'}
                        </td>
                        <td className="py-1 font-mono">{s.cathay_id_entered}</td>
                        <td className="py-1 text-slate-500">session{s.auto_closed ? ' (auto)' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- modals ---------------- */

function NewSessionModal({ prefill, year, mon, deviceId, deviceName, onClose, onSaved }) {
  const day = prefill?.day ?? 1;
  const hour = prefill?.hour ?? 0;
  const initialSignin = toDtLocalInput(new Date(year, mon - 1, day, hour, 0));
  const initialSignout = toDtLocalInput(new Date(year, mon - 1, day, hour + 1, 0));

  const [form, setForm] = useState({
    cathay_id_entered: prefill?.suggestedBooking?.cathay_id || '',
    signin_at_utc: initialSignin,
    signout_at_utc: initialSignout,
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const signinDate = fromDtLocalInput(form.signin_at_utc);
      const signoutDate = form.signout_at_utc ? fromDtLocalInput(form.signout_at_utc) : null;
      if (!signinDate) throw new Error('Invalid sign-in time');
      if (signoutDate && signoutDate.getTime() <= signinDate.getTime()) {
        throw new Error('Sign-out must be after sign-in');
      }
      await api.sessions.create({
        device_id: deviceId,
        cathay_id_entered: form.cathay_id_entered.trim(),
        signin_at_utc: signinDate.toISOString(),
        signout_at_utc: signoutDate ? signoutDate.toISOString() : null,
      });
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add actual crew sign-in" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="text-xs text-slate-500">
          Device: <span className="font-mono text-slate-700">{deviceName}</span>
        </div>
        {prefill?.suggestedBooking && (
          <div className="text-xs bg-cathay-50 border border-cathay-200 rounded p-2 text-cathay-800">
            Pre-filling CathayID from the booked pilot at this slot. Change it if a different
            crew actually used the IPT.
          </div>
        )}
        <Field label="CathayID">
          <input
            required
            autoFocus
            value={form.cathay_id_entered}
            onChange={(e) => setForm({ ...form, cathay_id_entered: e.target.value })}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
          />
        </Field>
        <DateTimeWithSlot
          label="Sign-in (24h)"
          value={form.signin_at_utc}
          onChange={(v) => setForm({ ...form, signin_at_utc: v })}
          kind="signin"
        />
        <DateTimeWithSlot
          label="Sign-out (24h, optional)"
          value={form.signout_at_utc}
          onChange={(v) => setForm({ ...form, signout_at_utc: v })}
          kind="signout"
          allowEmpty
        />
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded border">Cancel</button>
          <button disabled={busy} className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white text-sm px-3 py-1.5 rounded">
            Create session
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* =============================================================================
   CellEditorModal — unified view of everything that happened on one hour
   slot of one IPT. Two tabs:
     Actual usage (default) — every iPad ipt_sessions row for this slot.
                              Each row exposes Edit + Delete + "Add another"
                              so multi-occupancy is visible and per-row
                              editable.
     Booked                  — every bookings row for this slot, with Edit
                              + Delete (admin only on delete).
   The sub-edit modals (EditSessionModal / EditBookingModal / NewSessionModal)
   carry a `returnTo` cookie back to this modal so admins can keep working
   inside one cell without re-clicking it.
   ============================================================================= */
function CellEditorModal({
  day, hour, deviceName, cell, onEditSession, onEditBooking, onAddActual, onChanged, onClose,
}) {
  const [tab, setTab] = useState('actual');
  const sessions = cell.sessions || [];
  const bookings = cell.bookings || [];

  const deleteSession = async (s) => {
    if (!confirm(`Delete this sign-in session for ${s.cathay_id_entered}?`)) return;
    try { await api.sessions.delete(s.id); onChanged(); }
    catch (e) { alert(e.message); }
  };
  const deleteBooking = async (b) => {
    if (!confirm(`Delete this booking row for ${b.cathay_id}?`)) return;
    try { await api.bookings.delete(b.id); onChanged(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-xl w-full p-5">
        <header className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-cathay-700">Edit cell</h3>
            <div className="text-xs text-slate-500 mt-0.5">
              Day {day} · {String(hour).padStart(2, '0')}:00–{String((hour + 1) % 24).padStart(2, '0')}:00 ·{' '}
              <span className="font-mono">{deviceName || 'IPT'}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </header>

        <div className="flex gap-1 border-b border-slate-200">
          <CellTab active={tab === 'actual'} onClick={() => setTab('actual')}>
            Actual usage <span className="text-slate-400">({sessions.length})</span>
          </CellTab>
          <CellTab active={tab === 'booked'} onClick={() => setTab('booked')}>
            Booked <span className="text-slate-400">({bookings.length})</span>
          </CellTab>
        </div>

        {tab === 'actual' ? (
          <div className="mt-3 space-y-2">
            {sessions.length === 0 && (
              <p className="text-sm text-slate-500 py-3 text-center">No iPad sign-ins recorded on this slot.</p>
            )}
            {sessions.map((s) => (
              <CellRow
                key={s.id}
                badge="actual"
                cathay={s.cathay_id_entered}
                primary={
                  <>
                    {fmtTime(s.signin_at_utc)} →{' '}
                    {s.signout_at_utc
                      ? fmtTime(s.signout_at_utc)
                      : <em className="text-amber-600 not-italic">open</em>}
                  </>
                }
                secondary={s.auto_closed ? 'auto-closed' : s.status === 'voided' ? 'voided' : null}
                onEdit={() => onEditSession(s)}
                onDelete={() => deleteSession(s)}
              />
            ))}
            <button
              onClick={onAddActual}
              className="mt-1 bg-cathay-500 hover:bg-cathay-600 text-white text-sm px-3 py-1.5 rounded"
            >
              + Add actual crew sign-in
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {bookings.length === 0 && (
              <p className="text-sm text-slate-500 py-3 text-center">No booking covers this slot.</p>
            )}
            {bookings.map((b) => (
              <CellRow
                key={b.id}
                badge="booked"
                cathay={b.cathay_id}
                primary={`${String(b.start_hour).padStart(2, '0')}:00 – ${String(b.end_hour % 24).padStart(2, '0')}:00${b.end_hour > 24 ? ' (+1d)' : ''}`}
                secondary={b.pilot_name || null}
                onEdit={() => onEditBooking(b)}
                onDelete={() => deleteBooking(b)}
              />
            ))}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded border">Close</button>
        </div>
      </div>
    </div>
  );
}

function CellTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ' +
        (active ? 'border-cathay-600 text-cathay-700' : 'border-transparent text-slate-500 hover:text-slate-700')
      }
    >
      {children}
    </button>
  );
}

function CellRow({ badge, cathay, primary, secondary, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-2 border border-slate-200 rounded px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Pill kind={badge} label={cathay} />
          <span className="text-sm font-mono text-slate-700">{primary}</span>
        </div>
        {secondary && <div className="text-xs text-slate-500 mt-0.5">{secondary}</div>}
      </div>
      <div className="flex gap-2 shrink-0 text-xs">
        <button onClick={onEdit} className="text-cathay-700 hover:underline">Edit</button>
        <button onClick={onDelete} className="text-red-600 hover:underline">Delete</button>
      </div>
    </div>
  );
}

function EditSessionModal({ session, onClose, onSaved }) {
  const [form, setForm] = useState({
    cathay_id_entered: session.cathay_id_entered,
    signin_at_utc: toDtLocalInput(session.signin_at_utc),
    signout_at_utc: toDtLocalInput(session.signout_at_utc),
    status: session.status,
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const signinDate = fromDtLocalInput(form.signin_at_utc);
      const signoutDate = form.signout_at_utc ? fromDtLocalInput(form.signout_at_utc) : null;
      await api.sessions.patch(session.id, {
        cathay_id_entered: form.cathay_id_entered.trim(),
        status: form.status,
        signin_at_utc: signinDate ? signinDate.toISOString() : null,
        signout_at_utc: signoutDate ? signoutDate.toISOString() : null,
      });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm('Delete this session row?')) return;
    setBusy(true);
    try { await api.sessions.delete(session.id); onSaved(); } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <Modal title="Edit session" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <Field label="CathayID">
          <input
            value={form.cathay_id_entered}
            onChange={(e) => setForm({ ...form, cathay_id_entered: e.target.value })}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
          />
        </Field>
        <DateTimeWithSlot
          label="Sign-in (24h)"
          value={form.signin_at_utc}
          onChange={(v) => setForm({ ...form, signin_at_utc: v })}
          kind="signin"
        />
        <DateTimeWithSlot
          label="Sign-out (24h — blank = session still open)"
          value={form.signout_at_utc}
          onChange={(v) => setForm({ ...form, signout_at_utc: v })}
          kind="signout"
          allowEmpty
        />
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="open">open</option>
            <option value="closed">closed</option>
            <option value="voided">voided</option>
          </select>
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <ModalFooter onRemove={remove} onClose={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

/**
 * datetime-local input plus a live preview of the slot start/end that the
 * server will round to. Uses the shared @iptbc/session-rounding library so
 * the client preview matches what the server stores.
 */
function DateTimeWithSlot({ label, value, onChange, kind, allowEmpty }) {
  const dt = value ? fromDtLocalInput(value) : null;
  let rounded = null;
  if (dt) {
    rounded = kind === 'signin' ? roundSignIn(dt) : roundSignOut(dt);
  }
  return (
    <label className="block">
      <span className="text-xs text-slate-600">{label}</span>
      <input
        type="datetime-local"
        step="60"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={!allowEmpty}
        className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
      />
      {dt && rounded && (
        <div className="mt-1 text-[11px] text-slate-600 leading-tight">
          <div>
            Actual: <span className="font-mono text-slate-800">{fmtDateTime(dt)}</span>
          </div>
          <div>
            Slot {kind === 'signin' ? 'start' : 'end'}:{' '}
            <span className="font-mono text-cathay-700">{fmtDateTime(rounded.date)}</span>
          </div>
        </div>
      )}
    </label>
  );
}

function EditBookingModal({ booking, devices, onClose, onSaved }) {
  const [form, setForm] = useState({
    booking_date: String(booking.booking_date).slice(0, 10),
    start_hour: booking.start_hour,
    end_hour: booking.end_hour,
    cathay_id: booking.cathay_id,
    pilot_name: booking.pilot_name || '',
    ipt_device_id: booking.ipt_device_id || '',
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.bookings.patch(booking.id, {
        booking_date: form.booking_date,
        start_hour: Number(form.start_hour),
        end_hour: Number(form.end_hour),
        cathay_id: form.cathay_id.trim(),
        pilot_name: form.pilot_name.trim() || null,
        ipt_device_id: form.ipt_device_id || null,
      });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm('Delete this booking?')) return;
    setBusy(true);
    try { await api.bookings.delete(booking.id); onSaved(); } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <Modal title="Edit booking" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <Field label="Date">
          <input type="date" value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Start hour (0–23)">
            <input type="number" min={0} max={23} value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="End hour (1–48, >24 = next day)">
            <input type="number" min={1} max={48} value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>
        <Field label="CathayID">
          <input value={form.cathay_id} onChange={(e) => setForm({ ...form, cathay_id: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="Pilot name">
          <input value={form.pilot_name} onChange={(e) => setForm({ ...form, pilot_name: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Pinned device (blank = any IPT)">
          <select value={form.ipt_device_id} onChange={(e) => setForm({ ...form, ipt_device_id: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="">(any)</option>
            {devices.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <ModalFooter onRemove={remove} onClose={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <header className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-cathay-700">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </header>
        {children}
      </div>
    </div>
  );
}
function ModalFooter({ onRemove, onClose, busy }) {
  return (
    <div className="flex justify-between pt-2 border-t border-slate-100">
      <button type="button" onClick={onRemove} disabled={busy} className="text-sm text-red-600 hover:underline">Delete</button>
      <div className="space-x-2">
        <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded border">Cancel</button>
        <button disabled={busy} className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white text-sm px-3 py-1.5 rounded">Save</button>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
